import React from 'react';
import TileLayer from 'ol/layer/Tile';
import { Feature, Overlay } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import { Geometry, Polygon } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import GeoImage from 'ol-ext/source/GeoImage';
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';

import {
  getTopPoint,
  createCustomLabelPosition
} from '../../utils/map/label-position';
import { zoneStyle } from '../../components/map/map-canvas';
import { MapAdapter } from '../../components/map/mapAdapter';
import { MapContext } from '../../components/map/map-context';
import { MapEntity } from '../../types/game-document/entities';
import { createCustomTaskPosition } from '../../utils/map/task-position';
import { ExportedZoneData } from '../../types/game-document/entities/exported-zone';

export const ZoneLibraryWorldPreview: React.FC<{
  content: ExportedZoneData;
}> = ({ content }) => {
  const map = React.useContext(MapContext);

  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const imageLayersRef = React.useRef<Record<string, ImageLayer<GeoImage>>>({});

  const mapEntity = content.maps[0];

  const [isOpenStreetMap, setIsOpenStreetMap] = React.useState<boolean>(
    mapEntity.type === 'googleMaps' || mapEntity.type === 'openStreetMap'
      ? true
      : false
  );

  const imageEntities = content.mapImages ?? [];
  const zoneEntities = content.zones ?? [];
  const areaEntities = content.areas ?? [];
  const taskEntities = content.tasks ?? [];

  const imageList = React.useMemo(() => {
    const result: any[] = [];

    if (imageEntities.length > 0) {
      imageEntities.forEach((image) => {
        if (image.imageResId) {
          const url =
            content.resources.find((res) => res.id === image.imageResId)
              ?.value ?? '';
          result.push({
            id: `image-${image.id}`,
            url,
            opacity: image.opacity ?? 1,
            center: image.center ?? [0, 0],
            scale: image.scale ?? [100, 100],
            isVisible: image.isVisible,
            isLocked: image.isLocked ?? false
          });
        }
      });
    }

    return result;
  }, [imageEntities]);

  const buildTileLayer = (type: MapEntity['type']): TileLayer => {
    const resolvedType = type === 'openStreetMap' ? 'googleMaps' : type;
    const adapter = new MapAdapter({ type: resolvedType } as MapEntity);
    return adapter.baseLayer;
  };

  const onViewChange = (e: SwitchChangeEvent) => {
    const mapTypeChange = e.target.value;
    const mapType = mapTypeChange ? 'googleMaps' : 'satelliteMap';

    setIsOpenStreetMap(mapTypeChange);

    const tileLayer = buildTileLayer(mapType);

    const layers = map.getAllLayers();
    if (layers.length > 0) {
      layers[0] = tileLayer;
      map.setLayers(layers);
    } else {
      map.setLayers([tileLayer]);
    }

    map.getView();
  };

  React.useEffect(() => {
    if (!mapRef.current) return;

    // initialize map target
    map.setTarget(mapRef.current as HTMLElement);

    const tileLayer = buildTileLayer('openStreetMap');

    const zoneAreaSource = new VectorSource();
    const zoneAreaLayer = new VectorLayer({
      properties: {
        id: 'vectorZoneArea'
      },
      source: zoneAreaSource
    });

    map.setLayers([tileLayer, zoneAreaLayer]);

    // render zone
    zoneEntities.forEach((worldZone) => {
      let id = worldZone.boundary?.id;
      if (!id) return;
      id = `zone-${id}`;

      let zoneFeature = zoneAreaSource.getFeatureById(id) as Feature<Geometry>;

      if (!zoneFeature) {
        zoneFeature = new GeoJSON().readFeature(
          worldZone.boundary as never
        ) as Feature<Geometry>;
        zoneFeature.setId(id);
        zoneAreaSource.addFeature(zoneFeature);
      }

      zoneFeature.setStyle(zoneStyle(worldZone.isVisible, 'Zone'));

      // render zone label
      const labelOverlay = map.getOverlayById(id);
      if (!labelOverlay) {
        const title =
          content.resources.find((res) => res.id === worldZone.titleResId)
            ?.value ?? '';

        const labelElement = createCustomLabelPosition(id, title);

        const overlay = new Overlay({
          id,
          position: getTopPoint(
            (zoneFeature.getGeometry() as Polygon).getCoordinates()[0] as any
          ),
          positioning: 'center-center',
          element: labelElement,
          stopEvent: false
        });

        map.addOverlay(overlay);

        map.getView().fit(zoneFeature.getGeometry() as Polygon, {
          duration: 1000
        });
      }
    });

    // render area
    areaEntities.forEach((worldArea) => {
      let id = worldArea.boundary?.id;
      if (!id) return;
      id = `area-${id}`;

      let areaFeature = zoneAreaSource.getFeatureById(id) as Feature<Geometry>;

      if (!areaFeature) {
        areaFeature = new GeoJSON().readFeature(
          worldArea.boundary as never
        ) as Feature<Geometry>;

        areaFeature.setId(id);
        zoneAreaSource.addFeature(areaFeature);
      } else {
        const updatedGeometry = new GeoJSON().readFeature(
          worldArea.boundary as never
        ) as Feature<Geometry>;
        areaFeature.setGeometry(updatedGeometry.getGeometry());
      }

      areaFeature.setStyle(zoneStyle(worldArea.isVisible, 'Area'));

      const labelOverlay = map.getOverlayById(id);
      if (!labelOverlay) {
        const title =
          content.resources.find((res) => res.id === worldArea.titleResId)
            ?.value ?? '';

        const labelElement = createCustomLabelPosition(id, title);

        const overlay = new Overlay({
          id,
          position: getTopPoint(
            (areaFeature.getGeometry() as Polygon).getCoordinates()[0] as any
          ),
          positioning: 'center-center',
          element: labelElement,
          stopEvent: false
        });

        map.addOverlay(overlay);
      }
    });

    // render task
    taskEntities.forEach((task) => {
      if (!task.boundary?.geometry?.coordinates) return;
      const imgUrl =
        content.resources.find((res) => res.id === task.imageResId)?.value ??
        '';

      // Fallback to default designer icon if resource not found
      const resolvedImgUrl =
        imgUrl || 'https://cdn.catalystglobal.games/resources/map-task.png';

      const title =
        content.resources.find((res) => res.id === task.titleResId)?.value ??
        '';

      const taskExist = map.getOverlayById(task.id);
      // prevent create task overlay more than once
      if (taskExist) return;

      const taskElement = createCustomTaskPosition({
        id: task.id,
        name: title,
        iconUrl: resolvedImgUrl,
        isAddTaskMode: false,
        showTasknameVisibility: true,
        isVisible: Boolean(task.isVisible)
      });

      const overlay = new Overlay({
        id: task.id,
        position: task.boundary.geometry.coordinates || [0, 0],
        positioning: 'center-center',
        element: taskElement,
        stopEvent: false
      });

      map.addOverlay(overlay);
    });

    return () => {
      // clean up all overlays and layers when unmount
      map.getOverlays().clear();
      map.getLayers().clear();
      map.setTarget(undefined);
    };
  }, [mapRef, content, zoneEntities, areaEntities, taskEntities]);

  // render image
  React.useEffect(() => {
    const mapLayers = map.getAllLayers();
    if (mapLayers.length === 0 || !map) return;

    const layers = map.getAllLayers();
    const existingImageLayers = layers.filter((layer) =>
      layer.get('id')?.startsWith('image-')
    );
    existingImageLayers.forEach((layer) => map.removeLayer(layer));
    imageLayersRef.current = {};

    if (!imageList || imageList.length === 0) return;

    const imageLayers: ImageLayer<GeoImage>[] = [];

    // wait for all images to load before adding layers
    const promises = imageList.map(
      (imgData) =>
        new Promise<void>((resolve) => {
          const { id, url, center, scale, opacity = 1, isVisible } = imgData;

          const image = new Image();
          image.src = url;
          image.onload = () => {
            const source = new GeoImage({
              url,
              imageCenter: center,
              imageScale: scale
            });

            const layer = new ImageLayer({
              source,
              properties: { id },
              opacity,
              visible: isVisible
            });

            imageLayers.push(layer);
            imageLayersRef.current[id] = layer;

            resolve();
          };

          image.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve();
          };
        })
    );

    // once all images are loaded, update the layer order
    Promise.all(promises).then(() => {
      if (imageLayers.length > 0) {
        const tileLayer = layers[0];
        const otherLayers = layers
          .slice(1)
          .filter((layer) => !layer.get('id')?.startsWith('image-'));

        imageLayers.forEach((layer) => {
          const source = layer.getSource();
          const id = layer.get('id');
          if (!source || !id) return;

          const imageExtent = source.getExtent();
          const imageFeature = new Feature({
            geometry: new Polygon([
              [
                [imageExtent[0], imageExtent[1]],
                [imageExtent[0], imageExtent[3]],
                [imageExtent[2], imageExtent[3]],
                [imageExtent[2], imageExtent[1]],
                [imageExtent[0], imageExtent[1]]
              ]
            ])
          });

          imageFeature.setId(id);
          const imageLayer = map
            .getAllLayers()
            .find((l) => l.get('id') === 'vectorImage');

          if (imageLayer) {
            (imageLayer.getSource() as VectorSource).addFeature(imageFeature);
          }
        });

        // new order: tile → images → others
        map.setLayers([tileLayer, ...imageLayers, ...otherLayers]);
      }
    });

    return () => {
      imageLayers.forEach((layer) => map.removeLayer(layer));
      imageLayersRef.current = {};
    };
  }, [
    map,
    imageList.map((i) => i.id + i.url + i.opacity + i.center + i.scale).join('')
  ]);

  return (
    <div
      id={'map'}
      ref={mapRef as any}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative'
      }}>
      <div
        style={{
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 2,
          bottom: '5px',
          left: '5px',
          backgroundColor: 'white',
          borderRadius: '3px',
          padding: '5px 10px'
        }}>
        <span>Street View</span>
        <Switch
          size={'small'}
          onLabel={'Street View'}
          offLabel={'Satelite View'}
          onChange={(e) => onViewChange(e)}
          checked={isOpenStreetMap}
        />
      </div>
    </div>
  );
};
